import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import { confirmAnomaly, dismissAnomaly, getAnomalies, PfcError, type Anomaly, type AnomalyStatus } from "./api";
import { anomalyCountNote, anomalyKey, anomalyValue, bandLabel, CARD_COPY, CARD_KINDS, confirmWritesRate, COPY, messageFor, money, RATE_COMPONENTS, SHOPIFY_KINDS, signedPct, sortAnomalies, type AnomalyChannel } from "./format";

const STATUSES: Array<{ value: AnomalyStatus | "all"; label: string }> = [
  { value: "open", label: "Open" },
  { value: "confirmed", label: "Confirmed" },
  { value: "dismissed", label: "Dismissed" },
  { value: "all", label: "All" },
];

// C5 v2.5: one card per channel. Amazon (the default) keeps its kinds and copy; Shopify lists the parcel and lane checks.
export function AnomaliesCard({ filters, runId, channel = "amazon" }: { filters: { channel_location?: string; marketplace_id?: string }; runId: string | null; channel?: AnomalyChannel }) {
  const copy = CARD_COPY[channel];
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<AnomalyStatus | "all">("open");
  const [page, setPage] = useState(1);
  const [dismissing, setDismissing] = useState<number | null>(null);
  const [reason, setReason] = useState("");
  const [message, setMessage] = useState<{ tone: "ok" | "err"; text: string } | null>(null);
  const kindList = CARD_KINDS[channel].join(",");
  const list = useQuery({ queryKey: ["pfc", "anomalies", status, kindList, filters, page], queryFn: () => getAnomalies({ status, kind: kindList, ...filters, page }), retry: 1 });
  const listError = list.error;
  const listPending = list.isPending;
  const rows = sortAnomalies(list.data?.data.rows ?? []);
  const openCount = list.data?.data.open ?? 0;
  const pages = list.data?.data.pages ?? 1;
  const loaded = Boolean(list.data);
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ["pfc", "anomalies"] });
    void queryClient.invalidateQueries({ queryKey: ["pfc", "rates"] });
  };
  const confirm = useMutation({
    mutationFn: (anomaly: Anomaly) => confirmAnomaly(anomaly.id, {}),
    onSuccess: (result, anomaly) => {
      setMessage({
        tone: "ok",
        text: confirmWritesRate(anomaly) && result.data.rule_id !== null
          ? COPY.confirmedRate(`${anomaly.xero_sku} on ${anomaly.marketplace_name ?? anomaly.channel_location}`, anomalyValue(anomaly.reference_value, anomaly.component, anomaly.currency), anomalyValue(anomaly.billed_value, anomaly.component, anomaly.currency), result.data.version ?? 0, result.data.audit_id)
          : COPY.confirmedLane,
      });
      refresh();
    },
    onError: (error) => setMessage({ tone: "err", text: messageFor(error) }),
  });
  const dismiss = useMutation({
    mutationFn: (anomaly: Anomaly) => dismissAnomaly(anomaly.id, { reason: reason.trim() }),
    onSuccess: () => {
      setMessage({ tone: "ok", text: copy.dismissed });
      setDismissing(null);
      setReason("");
      refresh();
    },
    onError: (error) => setMessage({ tone: "err", text: messageFor(error) }),
  });

  if (listError) {
    const error = listError as PfcError;
    if (error.status === 404) return <Card><CardHeader className="font-bold">{copy.title}</CardHeader><CardContent className="text-ks-grey-1">{COPY.anomaliesOff}</CardContent></Card>;
    return <Card><CardHeader className="font-bold">{copy.title}</CardHeader><CardContent><p role="alert" className="text-ks-danger">{messageFor(error)}</p></CardContent></Card>;
  }

  const firstOpen = rows.find((row) => row.status === "open");
  return <Card>
    <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3">
      <span className="font-bold">{copy.title}</span>
      <span className="flex items-center gap-2 text-sm text-ks-grey-1">
        {loaded ? `${openCount} open` : ""}
        <Label className="flex items-center gap-1">Status
          <select data-nonmutating className="rounded-sm border border-ks-grey-3 px-2 py-1" value={status} onChange={(event) => { setStatus(event.target.value as AnomalyStatus | "all"); setPage(1); setDismissing(null); }}>
            {STATUSES.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
          </select>
        </Label>
      </span>
    </CardHeader>
    <CardContent className="flex flex-col gap-2">
      {message && <p role={message.tone === "err" ? "alert" : "status"} className={message.tone === "err" ? "text-ks-danger" : "text-ks-pass"}>{message.text}</p>}
      {status === "open" && !listPending && rows.length === 0
        ? <Badge variant="outline" className="w-fit">{copy.empty(runId ?? "")}</Badge>
        : <>
            <p className="text-sm text-ks-grey-1">{copy.help}</p>
            <table className="w-full text-sm">
              <thead><tr className="text-left text-ks-grey-1"><td role="columnheader" className="px-2">{copy.keyHeader}</td><td role="columnheader" className="px-2">Component</td><td role="columnheader" className="px-2 text-right">Billed</td><td role="columnheader" className="px-2 text-right">Reference</td><td role="columnheader" className="px-2 text-right">Change</td><td role="columnheader" className="px-2 text-right">Allowed band</td><td role="columnheader" className="px-2">Last seen</td><td role="columnheader" className="px-2">Status</td><td className="px-2"></td></tr></thead>
              <tbody>
                {listPending ? <tr><td colSpan={9} className="px-2 py-2 text-ks-grey-1">Loading…</td></tr> : rows.map((anomaly) => {
                  const key = anomalyKey(anomaly);
                  return <tr key={anomaly.id} className={anomaly.id === firstOpen?.id ? "bg-ks-purple-4" : undefined}>
                    <td className="px-2"><div className="font-bold">{key}</div><div className="text-xs text-ks-grey-1">{anomalyCountNote(anomaly)}</div></td>
                    <td className="px-2">{anomaly.component}</td>
                    <td className="px-2 text-right font-bold" title={anomaly.kind === "dsf_unmodelled" ? "per unit, no DSF rule" : SHOPIFY_KINDS.includes(anomaly.kind) ? `${anomaly.currency} net per order` : RATE_COMPONENTS.includes(anomaly.component) ? "rate of the settlement window" : `${anomaly.currency} net per unit`}>{anomaly.kind === "dsf_unmodelled" ? money(anomaly.billed_value, anomaly.currency) : anomalyValue(anomaly.billed_value, anomaly.component, anomaly.currency)}</td>
                    <td className="px-2 text-right">{anomaly.kind === "dsf_unmodelled" ? <>—<div className="text-xs text-ks-grey-1">no DSF rule</div></> : <>{anomalyValue(anomaly.reference_value, anomaly.component, anomaly.currency)}<div className="text-xs text-ks-grey-1">{anomaly.reference_kind.replace("_", " ")}</div></>}</td>
                    <td className="px-2 text-right font-bold text-ks-danger">{anomaly.kind === "dsf_unmodelled" ? "—" : signedPct(anomaly.change_pct)}</td>
                    <td className="px-2 text-right">{bandLabel(anomaly.band)}</td>
                    <td className="px-2">{anomaly.last_seen ?? "—"}{anomaly.seen_runs > 1 ? <div className="text-xs text-ks-grey-1">seen {anomaly.seen_runs} runs</div> : null}</td>
                    <td className="px-2">{anomaly.status === "open" ? <Badge variant="destructive">OPEN</Badge> : anomaly.status === "confirmed" ? <><Badge>confirmed</Badge><div className="text-xs text-ks-grey-1">{anomaly.actor} · {anomaly.resolved_at?.slice(0, 10)}{anomaly.audit_ref ? ` · audit ${anomaly.audit_ref}` : ""}</div></> : <><Badge variant="outline">dismissed</Badge><div className="text-xs text-ks-grey-1">{anomaly.actor} · {anomaly.resolved_at?.slice(0, 10)} · {anomaly.reason}</div></>}</td>
                    <td className="px-2">{anomaly.status !== "open" ? null : dismissing === anomaly.id
                      ? <div className="flex flex-col gap-1">
                          <Label htmlFor={`dismiss-${anomaly.id}`}>{COPY.dismissReason}</Label>
                          <Input id={`dismiss-${anomaly.id}`} value={reason} onChange={(event) => setReason(event.target.value)} autoFocus />
                          <div className="flex gap-2"><Button size="sm" disabled={!reason.trim() || dismiss.isPending} onClick={() => dismiss.mutate(anomaly)}>{copy.dismiss}</Button><Button size="sm" variant="outline" data-nonmutating onClick={() => { setDismissing(null); setReason(""); }}>Cancel</Button></div>
                        </div>
                      : <div className="flex gap-2">
                          <Button size="sm" disabled={confirm.isPending} aria-label={`${confirmWritesRate(anomaly) ? COPY.confirmRate : COPY.confirm} for ${key}`} onClick={() => { setMessage(null); confirm.mutate(anomaly); }}>{confirmWritesRate(anomaly) ? COPY.confirmRate : COPY.confirm}</Button>
                          <Button size="sm" variant="outline" aria-label={`${copy.dismiss} ${key}`} onClick={() => { setMessage(null); setDismissing(anomaly.id); setReason(""); }}>{copy.dismiss}</Button>
                        </div>}</td>
                  </tr>;
                })}
              </tbody>
            </table>
            {pages > 1 && <div className="flex items-center justify-end gap-2 text-sm text-ks-grey-1">
              <span>{COPY.anomaliesPage(page, pages)}</span>
              <Button size="sm" variant="outline" data-nonmutating disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</Button>
              <Button size="sm" variant="outline" data-nonmutating disabled={page >= pages} onClick={() => setPage(page + 1)}>Next</Button>
            </div>}
          </>}
    </CardContent>
  </Card>;
}
